import React, { useEffect, useState } from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RefreshCcw } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import DirhamsIcon from '@/components/ui/dirhams-icon';
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from '@/components/ui/table-styles';
import { PurchaseOrderItemResponse } from '@/gen/purchase_order/common_pb';
import { InventoryLineItem } from '@/gen/inventory/common_pb';
import { convertToCurrencyRounded } from '@/utils/currency_converter';
import { useRouter } from '@tanstack/react-router';
import { Level } from '@/gen/auth/common_pb';
import db from '@/db';
import dayjs from 'dayjs';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { pharmacyClient } from '@/connect';

const UpdateStatusDialog = ({type, document_number, document_id, poItems, inventoryItems, initialisedFrom, supplier_id, stock_point_name}: {
  type: 'PO' | 'PI' | 'PR',
  document_number: string,
  poItems?: PurchaseOrderItemResponse[]
  inventoryItems?: InventoryLineItem[],
  initialisedFrom: Level,
  document_id: string,
  supplier_id: string,
  stock_point_name?: string
}) => {

  const [checked, setChecked] = useState<boolean[]>(
    type === 'PO' ? (poItems?.map(() => false) || []) : (inventoryItems?.map(() => false) || [])
  );

  const [createPurchaseReturnSelected, setCreatePurchaseReturnSelected] = useState<boolean>(false);
  const [showNotesInput, setShowNotesInput] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');

  const router = useRouter();

  const allChecked = checked.every(Boolean);

  const handleCheck = (idx: number) => {
    setChecked((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const handleCheckAll = (value: boolean) => {
    setChecked(
      (prev) => prev.map(() => value)
    );
  };

  useEffect(() => {
    // If all items are unchecked, reset createPurchaseReturnSelected
    if (!checked.some(Boolean)) {
      setCreatePurchaseReturnSelected(false);
      setChangedReturnItems([]);
    }
  }, [checked]);

  const [changedReturnItems, setChangedReturnItems] = useState<InventoryLineItem[]>([]);

  const handleCreateInvoice = async () => {
    const selectedItems = poItems?.filter((_, idx) => checked[idx]);
    if (selectedItems && selectedItems.length > 0) {
      const pi_instance_number = await db.create_pi_instance.add({
        complete: false,
        updated_at: new Date(),
        type: 'CREATE_PI',
      })
      await db.create_pi_info.add({
        instance_id: pi_instance_number,
        instance_type: 'CREATE_PI',
        po_id: document_id,
        supplier_id: supplier_id,
      })
      await db.add_stock_item.bulkAdd(
        selectedItems.map((item) => ({
          instance_id: pi_instance_number,
          instance_type: 'CREATE_PI',
          item_id: item.masterId,
          product_name: item.tradeName,
          type: item.itemType.toString(),
          batch_no: "",
          cost: item.unitPrice ?? 0,
          price: item.unitPrice ?? 0,
          expiry_date: new Date().toISOString(),
          production_date: new Date().toISOString(),
          quantity: item.quantity ?? 0,
          bonus: item.bonusQuantity ?? 0,
          tax_profile: item.taxProfile?.toJsonString() || '',
          discount_type: "",
          discount_value: 0,
          custom_barcode: "",
          scientific_name: item.tradeName || '',
          unit: item.unit.toString() || '',
          unit_type: item.unitType || '',
        }))
      );
      router.navigate({
        to: initialisedFrom === Level.STOCK_POINT ? '/inventory/purchase-invoice/create' : '/branch/purchase-invoice/create',
        search: {
          instance_id: pi_instance_number
        }
      })
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <RefreshCcw className="w-3 h-3 mr-1" />Update Status
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] w-full">
        <DialogHeader>
          <DialogTitle>Update Status</DialogTitle>
          <DialogDescription>
            This is for the selected {type === 'PO' ? 'Purchase Order' : type === 'PI' ? 'Purchase Invoice' : 'Purchase Return'} '{document_number}'.
            <br />
            {type === 'PO' && (
              <span className="text-xs text-gray-600">
                Select items to convert to Purchase Invoice.
              </span>
            )}
            {type === 'PI' && (
                <span className="text-xs text-gray-600">
                  Please select the items to be returned. Kindly initiate an Inventory Transfer to transfer the required items to <span className="font-semibold">{stock_point_name}</span> prior to proceeding. Otherwise, the available quantity may be insufficient for the intended return.
                </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 min-h-[300px] max-h-[350px] overflow-y-auto border rounded-md">
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead className={tableHeadClass + ' w-8 text-center p-0'}>
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={(v) => handleCheckAll(!!v)}
                  />
                </TableHead>
                <TableHead className={tableHeadClass}>SL NO.</TableHead>
                <TableHead className={tableHeadClass}>Product Name</TableHead>
                <TableHead className={tableHeadClass}>Unit</TableHead>
                <TableHead className={tableHeadClass}>Unit Type</TableHead>
                {type != "PO" && <TableHead className={tableHeadClass}>Batch</TableHead>}
                <TableHead className={tableHeadClass}>Barcode</TableHead>
                {type != "PO" && <TableHead className={tableHeadClass}>Expiry Date</TableHead>}
                <TableHead className={tableHeadClass}>Quantity</TableHead>
                <TableHead className={tableHeadClass}>Bonus</TableHead>
                {type != "PO" && <TableHead className={tableHeadClass}>{
                    createPurchaseReturnSelected ? 'Return Quantity' : 'Available Quantity at Stock Point'
                  }</TableHead>}
                <TableHead className={tableHeadClass}>Net Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {poItems?.map((item, idx) => (
                <TableRow key={item.masterId}>
                  <TableCell className={tableCellClass + ' text-center p-0'}>
                    <div className="flex justify-center items-center">
                      <Checkbox checked={checked[idx]} onCheckedChange={() => handleCheck(idx)} />
                    </div>
                  </TableCell>
                  <TableCell className={tableCellClass}>{idx + 1}</TableCell>
                  <TableCell className={tableCellClass}>{item.tradeName}</TableCell>
                  <TableCell className={tableCellClass}>{item.unit}</TableCell>
                  <TableCell className={tableCellClass}>{item.unitType}</TableCell>
                  <TableCell className={tableCellClass}>{"N/A"}</TableCell>
                  <TableCell className={tableCellClass}>{item.quantity}</TableCell>
                  <TableCell className={tableCellClass}>{item.bonusQuantity}</TableCell>
                  <TableCell className={tableCellClass}>{
                      convertToCurrencyRounded(item.unitPrice * item.quantity)
                    }</TableCell>
                </TableRow>
              ))}
              {inventoryItems?.map((item, idx) => (
                <TableRow key={item.masterId}>
                  <TableCell className={tableCellClass + ' text-center p-0'}>
                    <div className="flex justify-center items-center">
                      <Checkbox checked={checked[idx]} onCheckedChange={() => handleCheck(idx)} />
                    </div>
                  </TableCell>
                  <TableCell className={tableCellClass}>{idx + 1}</TableCell>
                  <TableCell className={tableCellClass}>{item.tradeName}</TableCell>
                  <TableCell className={tableCellClass}>{item.unit}</TableCell>
                  <TableCell className={tableCellClass}>{item.unitType}</TableCell>
                  <TableCell className={tableCellClass}>{item.unitType}</TableCell>
                  <TableCell className={tableCellClass}>{item.barcode}</TableCell>
                  <TableCell className={tableCellClass}>{dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY")}</TableCell>
                  <TableCell className={tableCellClass}>{item.inputQuantity?.original}</TableCell>
                  <TableCell className={tableCellClass}>{item.inputQuantity?.bonus}</TableCell>
                  {!createPurchaseReturnSelected && <TableCell className={tableCellClass}>{item.availableQuantity}</TableCell>}
                  {createPurchaseReturnSelected && <TableCell className={tableCellClass}>
                      <Input
                        type="number"
                        value={changedReturnItems[idx]?.availableQuantity || item.availableQuantity}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          if (!isNaN(newValue)) {
                            if (newValue > item.availableQuantity) {
                              toast({
                                title: "Invalid Quantity",
                                description: `You cannot enter a quantity greater than the available quantity (${item.availableQuantity})`,
                                variant: 'destructive',
                                duration: 3000,
                              });
                              return;
                            }
                            setChangedReturnItems((prev) => {
                              const updatedItems = [...prev];
                              const clonedItem = item.clone();
                              clonedItem.availableQuantity = newValue;
                              updatedItems[idx] = clonedItem;
                              return updatedItems;
                            });
                          } else {
                            // Set to 0
                            setChangedReturnItems((prev) => {
                              const updatedItems = [...prev];
                              const clonedItem = item.clone();
                              clonedItem.availableQuantity = 0;
                              updatedItems[idx] = clonedItem;
                              return updatedItems;
                            });
                          }
                        }}
                        max={item.availableQuantity}
                        min={0}
                        className="w-full"
                        placeholder="Enter quantity"
                      />
                    </TableCell>}
                  <TableCell className={tableCellClass}>{
                      convertToCurrencyRounded(item.costFromSupplier * item.inputQuantity?.original!)
                    }</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-6 gap-2">
          {/* <Button variant="outline">Mark as not Received</Button> */}
          {checked.filter(Boolean).length > 0 && <div className="flex-1 flex justify-center">
            <div className="flex items-center gap-3 bg-white shadow-lg rounded-full px-8 py-2 border">
              <span className="bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">{checked.filter(Boolean).length}</span>
              <span className="text-xs text-gray-500">Selected</span>
              {type === "PO" && <Button size="sm" variant="outline" className="flex items-center gap-1 h-7 px-3 text-xs" onClick={handleCreateInvoice}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Create Purchase Invoice
              </Button>}
              {type === "PI" && !createPurchaseReturnSelected && <Button size="sm" variant="outline" className="flex items-center gap-1 h-7 px-3 text-xs" onClick={() => setCreatePurchaseReturnSelected(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Create Purchase Return
              </Button>}
              {type === "PI" && createPurchaseReturnSelected && <Button size="sm" variant="outline" className="flex items-center gap-1 h-7 px-3 text-xs" onClick={() => {
                setCreatePurchaseReturnSelected(false)
                setChangedReturnItems([]);
                setChecked(inventoryItems?.map(() => false) || []);
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancel Return Creation
              </Button>}
              <Button size="sm" variant="outline" className="flex items-center gap-1 h-7 px-3 text-xs">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v16m16-16v16" /></svg>
                Repeat Purchase Order
              </Button>
            </div>
          </div>}
          {createPurchaseReturnSelected && (
            <>
              {!showNotesInput ? (
                <Button
                  className="bg-blue-700 text-white hover:bg-blue-800"
                  disabled={!changedReturnItems.every(item => item.availableQuantity > 0)}
                  onClick={() => setShowNotesInput(true)}
                >
                  Confirm Purchase Return
                </Button>
              ) : (
                <div className="flex flex-col gap-2 w-full max-w-xs">
                  <Input
                    type="text"
                    placeholder="Enter notes for purchase return"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowNotesInput(false);
                        setNotes('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-blue-700 text-white hover:bg-blue-800"
                      disabled={!changedReturnItems.every(item => item.availableQuantity > 0) || !notes.trim()}
                      onClick={async () => {
                          try {
                            await pharmacyClient.createPurchaseReturn({
                              supplierId: supplier_id,
                              notes: notes.trim(),
                              items: changedReturnItems.map(item => ({
                                inventoryId: item.inventoryId,
                                returnedQuantity: item.availableQuantity,
                                itemType: item.itemType,
                              }))
                            })
                            toast({
                              title: "Success",
                              description: "Purchase return created successfully.",
                              variant: 'success',
                              duration: 3000,
                            });
                            setShowNotesInput(false);
                            setNotes('');
                            setCreatePurchaseReturnSelected(false);
                            setChangedReturnItems([]);
                            setChecked(inventoryItems?.map(() => false) || []);
                            router.invalidate()
                          } catch (error) {
                            console.error("Error creating purchase return:", error);
                            toast({
                              title: "Error",
                              description: "Failed to create purchase return. Please try again.",
                              variant: 'destructive',
                              duration: 3000,
                            });
                          }
                      }}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateStatusDialog; 